import {
  Component,
  PLATFORM_ID,
  Renderer2,
  DOCUMENT,
  inject,
  computed,
  afterNextRender,
  effect,
} from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet, NavigationStart, NavigationCancel, NavigationError, Scroll } from '@angular/router';
import { Store } from '@ngrx/store';
import { DictionaryState, FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
import { getDictionaryLoaded } from '@teamfoster/sdk/dictionary-ngrx';

import * as navActions from './store/actions/nav.action';
import * as navSelectors from './store/selectors/nav.selector';
import * as breadCrumbSelectors from './store/selectors/breadcrumb.selector';
import * as selectors from './content/store/selectors/content-pages.selector';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter, map } from 'rxjs/operators';
import { SetTheme } from './store';
import { CookieConfigService, CookieNoticeComponent, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { toSignal } from '@angular/core/rxjs-interop';
import { TopbarComponent } from './core/components/topbar/topbar.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { LoaderIndicatorComponent } from './core/components/loading-indicator/loading-indicator.component';
import { BreadcrumbsComponent } from './core/components/breadcrumbs/breadcrumbs.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [],
  standalone: true,
  imports: [
    RouterOutlet,
    TopbarComponent,
    FooterComponent,
    LoaderIndicatorComponent,
    CookieNoticeComponent,
    FromDictionaryPipe,
    BreadcrumbsComponent,
  ],
})
export class AppComponent {
  #router = inject(Router);
  #platformId = inject(PLATFORM_ID);
  #store = inject(Store<DictionaryState>);
  #renderer = inject(Renderer2);
  #cookieConfig = inject(CookieConfigService);
  #document = inject(DOCUMENT);
  #breakpoints = inject(BreakpointObserver);

  dictionaryLoaded$ = this.#store.selectSignal(getDictionaryLoaded);
  menuItems$ = this.#store.selectSignal(selectors.getMenuPages);
  footerItems$ = this.#store.selectSignal(selectors.getFooterPages);
  breadcrumbs$ = this.#store.selectSignal(breadCrumbSelectors.getBreadCrumbs);
  menuOpen$ = this.#store.selectSignal(navSelectors.getMenuOpen);
  cookiePrefs$ = this.#store.selectSignal(getCookiePreferences);

  // breadcrumbs are rendered once: in the topbar from md up, below it on smaller screens
  isDesktop$ = toSignal(this.#breakpoints.observe('(min-width: 768px)').pipe(map(state => state.matches)), { initialValue: true });

  loading$ = computed(() => !this.dictionaryLoaded$());

  private routerEvents$ = toSignal(
    this.#router.events.pipe(
      filter(e => [NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Scroll].some(b => e instanceof b))
    )
  );

  // test
  gridColumns = [...Array(12).keys()];
  testOverlay = false;

  private previousPath = '';

  menuOpenEffect$ = effect(() => this.updateRoot(this.menuOpen$()));

  routerEffect$ = effect(() => {
    const event = this.routerEvents$();

    //console.log(event);

    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (event instanceof NavigationEnd && this.cookiePrefs$()?.analytical) {
      if ((<any>window).gtag) {
        (<any>window).gtag('config', this.#cookieConfig.analyticsCode, { page_path: event.urlAfterRedirects, anonymize_ip: true });
      }
    }
  });

  constructor() {
    afterNextRender(() => {});
    if (isPlatformBrowser(this.#platformId)) {
      this.#document.querySelector('body')?.classList.add('set--in');
      this.#store.dispatch(SetTheme({ theme: 'standard' }));
    }
  }

  getPageTransition(routerOutlet: RouterOutlet) {
    if (routerOutlet.isActivated) {
      let transitionName = 'section';

      let path = routerOutlet.activatedRoute.routeConfig?.path || '';

      const isSame = this.previousPath === path;
      const isBackward = this.previousPath.startsWith(path);
      const isForward = path.startsWith(this.previousPath);

      if (isSame) {
        transitionName = 'none';
      } else if (isBackward && isForward) {
        transitionName = 'initial';
      } else if (isBackward) {
        transitionName = 'backward';
      } else if (isForward) {
        transitionName = 'forward';
      }

      this.previousPath = path;

      return transitionName;
    }
    return undefined;
  }

  toggleMenu() {
    this.#store.dispatch(navActions.ToggleMenu());
  }

  closeMenu() {
    this.#store.dispatch(navActions.CloseMenu());
  }

  openMenu() {
    this.#store.dispatch(navActions.OpenMenu());
  }

  private updateRoot(menuActive: boolean) {
    menuActive
      ? this.#renderer.addClass(this.#document.body, 'main-nav--active')
      : this.#renderer.removeClass(this.#document.body, 'main-nav--active');
  }
}
